// Story 1.20 — TanStack mutation for POST /api/v1/account/portability-export.
//
// Structurally identical to useRequestDataExport — different endpoint,
// otherwise the same per-submit `crypto.randomUUID()` + cache-invalidation
// pattern.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RequestPortabilityResponse = {
  export_request_id: string;
  status: "pending";
  estimated_ready_in_minutes: number;
};

export type RequestPortabilityError = {
  code: string;
  message: string;
};

async function postPortabilityExportRequest(
  idempotencyKey: string,
): Promise<RequestPortabilityResponse> {
  const response = await fetch("/api/v1/account/portability-export", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: "{}",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RequestPortabilityError = {
      code: "PORTABILITY_EXPORT_REQUEST_FAILED",
      message: `Portability-export request failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: RequestPortabilityResponse;
  };
  return envelope.data;
}

export function useRequestPortabilityExport() {
  const queryClient = useQueryClient();
  return useMutation<RequestPortabilityResponse, RequestPortabilityError, void>(
    {
      mutationFn: () => postPortabilityExportRequest(crypto.randomUUID()),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["dsar-status"] });
      },
    },
  );
}
